import React from 'react'
import clsx from 'clsx'
import { FormattedMessage, useIntl } from 'react-intl'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
  Link as MuiLink,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Tooltip,
} from '@material-ui/core'
import ClearIcon from '@material-ui/icons/Clear'
import EditIcon from '@src/icons/EditIcon'
import messages from '../../../messages'
import styles from '@src/routes/Configurators/utils/styles'
import SubtotalAndAddToCart from './SubtotalAndAddToCart'
import { priceFormatter } from '@src/routes/Configurators/utils/PriceFormatter'
import { configContext } from '../Ngso'
import { Modifications, Products } from '../enum'
import EditOligoName from './EditOligoName'
import ConfirmRemovalPopup from '../../../components/ConfirmRemovalPopup'

const useStyles = makeStyles((theme: Theme) => {
  return {
    title: {
      backgroundColor: theme.palette.background.grey,
      fontWeight: theme.typography.fontWeightBold,
    },
    rootRow: {
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.pxToRem(12),
    },
    tableBorder: {
      borderTop: `1px solid ${theme.palette.grey[400]}`,
    },
    tabledataText: {
      fontSize: theme.typography.pxToRem(14),
      borderCollapse: 'unset',
    },
    tableDataLink: {
      color: theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightBold,
    },
    editIcon: {
      fontSize: theme.typography.pxToRem(16),
    },
    priceText: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(2.5),
      fontSize: theme.typography.pxToRem(18),
      fontWeight: theme.typography.fontWeightBold,
      width: '100%',
      justifyContent: 'end',
    },
    priceTotal: {
      width: '35%',
      justifyContent: 'space-between',
    },
    gridHead: {
      backgroundColor: theme.palette.background.grey,
    },
    list: {
      margin: 0,
      paddingLeft: 0,
    },
    heading: {
      marginTop: theme.spacing(5.5),
      marginBottom: theme.spacing(4),
      fontSize: theme.typography.pxToRem(18),
      fontWeight: theme.typography.fontWeightBold,
    },
    subHeading: {
      fontWeight: theme.typography.fontWeightRegular,
    },
    deleteAll: {
      color: theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.pxToRem(14),
    },
    sectionTitle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    tabletextAlign: {
      textAlign: 'right',
    },
    arrow: {
      color: theme.palette.common.white,
    },
    tooltip: {
      maxWidth: theme.typography.pxToRem(600),
      backgroundColor: theme.palette.common.white,
      fontSize: theme.typography.pxToRem(12),
      color: '#151515',
      boxShadow: theme.shadows[5],
      display: 'table',
      wordBreak: 'break-all',
      marginTop: theme.spacing(0.5),
      borderRadius: theme.typography.pxToRem(3),
      padding: theme.spacing(8),
    },
    sequenceLabel: {
      fontSize: theme.typography.pxToRem(20),
      fontWeight: theme.typography.fontWeightBold,
      paddingBottom: theme.spacing(8),
      textTransform: 'uppercase',
    },
    sequenceInfo: {
      fontWeight: 'normal',
      fontSize: theme.typography.pxToRem(14),
    },
    sequenceText: {
      fontWeight: theme.typography.fontWeightBold,
      width: theme.typography.pxToRem(250),
    },
    priceAmountAlign: {
      paddingRight: theme.spacing(4),
    },
    tableContainer: {
      maxHeight: 520,
    },
    cursorPointer: {
      cursor: 'pointer',
    },
    tableDataLinkOverride: {
      color: theme.palette.text.primary,
    },
    ngsoMixedCellItem: {
      paddingTop: theme.spacing(4),
    },
    deleteButtonMixedPlate: {
      verticalAlign: 'top',
      paddingTop: 20,
    },
  }
})

export const PlateOrderDetails = (props) => {
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [selectedName, setSelectedName] = React.useState()
  const [selectedIndex, setSelectedIndex] = React.useState()
  const classes = useStyles()
  const GlobalCSS = styles()
  const intl = useIntl()
  const {
    selectedPlate,
    productDetails,
    changeSelectedPlate,
    sessionData,
    totalValidation,
    clearSession,
    saveSessionHandler,
    configuratorAddToMiniCart,
  } = props

  const { state } = React.useContext(configContext)
  const { productInformation } = state
  const [delDialogOpen, setDelDialogOpen] = React.useState(false)
  const [ngsoToEmpty, setNgsoToEmpty] = React.useState(false)
  const [delItem, setDelItem] = React.useState()
  const [delType, setDelType] = React.useState('')
  const [plateTotalPrice, setPlateTotalPrice] = React.useState<string>('--')
  const [subTotalPrice, setSubTotalPrice] = React.useState<string>('--')

  const deleteSelectedItem = (item1) => {
    if (sessionData[selectedPlate].wellList.length <= 70) {
      setNgsoToEmpty(true)
      setDelItem(item1)
      setDelType('All')
      setDelDialogOpen(true)
    } else {
      setDelItem(item1)
      setDelType('Selected')
      setDelDialogOpen(true)
      setNgsoToEmpty(false)
    }
  }

  const handlePrice = (totalPrice, callbackFn) => {
    if (!totalPrice) {
      callbackFn('--')
      return
    }
    const formattedTotalPrice = priceFormatter(
      totalPrice,
      productInformation.currencyFormat,
      productInformation.thousandSeparator,
      productInformation.thousandSpacing,
      productInformation.decimalSeparator,
      productInformation.prefixCurrecny,
      sessionData[selectedPlate]?.wellList[0]?.currencySymbol
    )
    formattedTotalPrice !== Modifications.NaN
      ? callbackFn(formattedTotalPrice)
      : callbackFn('--')
  }

  React.useEffect(() => {
    let totalPrice = 0
    let subtotal = 0
    let plateQuote = 0
    let quoteItem = 0
    // Calculating the total plate price of selected plate
    if (sessionData[selectedPlate]?.wellList) {
      sessionData[selectedPlate].wellList.forEach((well) => {
        if (well.price === Modifications.priceQuote) {
          plateQuote = plateQuote + 1
        }
      })
      if (plateQuote !== 0) {
        setPlateTotalPrice(intl.formatMessage(messages._PC_QUOTE_ITEM))
        setSubTotalPrice(intl.formatMessage(messages._PC_QUOTE_ITEM))
      } else {
        sessionData[selectedPlate].wellList.forEach((well) => {
          totalPrice = totalPrice + parseFloat(well.price)
        })
        handlePrice(totalPrice, setPlateTotalPrice)
      }
    }
    // Calculating the price of total number of plates in session
    sessionData.forEach((plate) => {
      plate.wellList.forEach((well) => {
        if (well.price === Modifications.priceQuote) {
          quoteItem = quoteItem + 1
        }
      })
    })
    if (quoteItem !== 0) {
      setSubTotalPrice(intl.formatMessage(messages._PC_QUOTE_ITEM))
    } else {
      sessionData.forEach((plate) => {
        plate.wellList.forEach((well) => {
          subtotal = subtotal + parseFloat(well.price)
        })
      })
      handlePrice(subtotal, setSubTotalPrice)
    }
    // TODO: need a test before we refactor these dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlate, sessionData])

  const isItMixedNGSOPlate =
    sessionData[selectedPlate].plateType === 'MIXED' &&
    sessionData[selectedPlate].productType.includes(Products.Ngso)

  const deleteOligoItem = (item1, type) => {
    if (
      type === 'Selected' &&
      sessionData[selectedPlate].wellList.length !== 1
    ) {
      if (isItMixedNGSOPlate) {
        for (
          let i = sessionData[selectedPlate].wellList.length - 1;
          i >= 0;
          i--
        ) {
          if (
            sessionData[selectedPlate].wellList[i].row === item1.row &&
            sessionData[selectedPlate].wellList[i].column === item1.column
          ) {
            sessionData[selectedPlate].wellList.splice(i, 1)
          }
        }
      } else {
        const index = sessionData[selectedPlate].wellList.indexOf(item1)
        if (index !== -1) {
          sessionData[selectedPlate].wellList.splice(index, 1)
        }
      }

      totalValidation(sessionData, true)
    } else if (
      sessionData[selectedPlate].wellList.length === 1 ||
      type === 'All'
    ) {
      if (
        (sessionData[selectedPlate].wellList.length === 1 &&
          sessionData.length === 1) ||
        type === 'All'
      ) {
        clearSession(true)
        return
      }

      const index = sessionData.indexOf(sessionData[selectedPlate])
      if (index !== -1) {
        sessionData.splice(index, 1)
      }
      if (sessionData.length <= selectedPlate) {
        changeSelectedPlate(selectedPlate - 1)
      }
      saveSessionHandler(sessionData, false)
    }
  }
  const deleteAllHandler = () => {
    setDelItem(sessionData)
    setDelType('All')
    setDelDialogOpen(true)
    setNgsoToEmpty(false)
  }
  const handleOligoname = (item, index) => {
    setDialogOpen(true)
    setSelectedName(item)
    setSelectedIndex(index)
  }

  const UpdateNewName = (newname, index) => {
    sessionData[selectedPlate].wellList[index].name = newname
    saveSessionHandler(sessionData, false)
  }
  const mixedWellListHalfLength = Math.ceil(
    sessionData[selectedPlate]?.wellList.length / 2
  )
  const rawWellList = sessionData[selectedPlate]?.wellList
  const modifiedWellList = isItMixedNGSOPlate
    ? rawWellList.slice(0, mixedWellListHalfLength)
    : rawWellList
  return (
    <div>
      <div className={clsx(classes.heading, classes.sectionTitle)}>
        <div>
          <span>{sessionData[selectedPlate]?.plateName}</span>
          &nbsp;
          <span className={classes.subHeading}>
            ({sessionData[selectedPlate]?.wellList?.length}&nbsp;
            <FormattedMessage
              id="CONF_NGSO_OLIGO_COUNT"
              defaultMessage="Oligos"
            />
            )
          </span>
        </div>
        <div className={clsx(classes.deleteAll, classes.cursorPointer)}>
          <MuiLink onClick={() => deleteAllHandler()}>
            <FormattedMessage {...messages.CONF_DELETE_ALL_BUTTON} />
          </MuiLink>
        </div>
      </div>
      <TableContainer className={classes.tableContainer}>
        <Table className={classes.tableBorder}>
          <TableHead className={classes.title}>
            <TableRow>
              <TableCell align="left" className={classes.rootRow}>
                <FormattedMessage {...messages.CONF_NGSO_WELL_ID} />
              </TableCell>
              <TableCell align="left" className={classes.rootRow}>
                <FormattedMessage {...messages.CONF_NGSO_OLIGO_LABEL} />
              </TableCell>
              <TableCell align="left" className={classes.rootRow}>
                <FormattedMessage {...messages.CONF_SEQUENCE_LABEL} />
              </TableCell>
              <TableCell align="right" className={classes.rootRow}>
                <FormattedMessage {...messages.PRICE} />
              </TableCell>
              <TableCell align="right">
                <div></div>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {modifiedWellList.map((item, index) => {
              const matchingCell = isItMixedNGSOPlate
                ? rawWellList[index + mixedWellListHalfLength]
                : null
              return (
                <TableRow className={classes.tabledataText} key={index}>
                  <TableCell>
                    {item.row ? item.row : '--'}
                    {item.column ? item.column : '--'}
                  </TableCell>

                  <TableCell
                    className={`${classes.tableDataLink} ${
                      isItMixedNGSOPlate ? classes.tableDataLinkOverride : ''
                    }`}
                  >
                    {matchingCell ? (
                      <>
                        <div>{item.name ? item.name : '--'}</div>
                        <div className={classes.ngsoMixedCellItem}>
                          {matchingCell.name ? matchingCell.name : '--'}
                        </div>
                      </>
                    ) : (
                      <MuiLink
                        onClick={() => handleOligoname(item.name, index)}
                        className={classes.cursorPointer}
                      >
                        <span>{item.name ? item.name : '--'}</span> &nbsp;&nbsp;
                        <EditIcon className={classes.editIcon} />
                      </MuiLink>
                    )}
                  </TableCell>
                  <TableCell className={classes.tableDataLink}>
                    <Tooltip
                      title={
                        <>
                          <div className={classes.sequenceLabel}>
                            <FormattedMessage
                              {...messages.CONF_SEQUENCE_LABEL}
                            />
                          </div>
                          <div className={classes.sequenceInfo}>
                            {item.sequence ? (
                              <>
                                {item.fiveMod &&
                                item.fiveMod !== Modifications.None
                                  ? item.fiveMod
                                  : ''}
                                {item.sequence}
                                {item.threeMod &&
                                item.threeMod !== Modifications.None
                                  ? item.threeMod
                                  : ''}
                              </>
                            ) : (
                              '--'
                            )}
                          </div>
                        </>
                      }
                      interactive
                      classes={{
                        tooltip: classes.tooltip,
                        arrow: classes.arrow,
                      }}
                      placement="bottom-start"
                    >
                      <MuiLink
                        color="primary"
                        className={clsx(
                          GlobalCSS.seqWordWrapwithDot,
                          classes.sequenceText
                        )}
                      >
                        {item.sequence ? (
                          <>
                            {item.fiveMod !== Modifications.None
                              ? item.fiveMod
                              : ''}
                            {item.sequence}
                            {item.threeMod !== Modifications.None
                              ? item.threeMod
                              : ''}{' '}
                          </>
                        ) : (
                          '--'
                        )}
                      </MuiLink>
                    </Tooltip>
                    {matchingCell && (
                      <div className={classes.ngsoMixedCellItem}>
                        <Tooltip
                          title={
                            <>
                              <div className={classes.sequenceLabel}>
                                <FormattedMessage
                                  {...messages.CONF_SEQUENCE_LABEL}
                                />
                              </div>
                              <div className={classes.sequenceInfo}>
                                {matchingCell?.sequence ? (
                                  <>
                                    {matchingCell.fiveMod &&
                                    matchingCell.fiveMod !== Modifications.None
                                      ? matchingCell.fiveMod
                                      : ''}
                                    {matchingCell.sequence}
                                    {matchingCell.threeMod &&
                                    matchingCell.threeMod !== Modifications.None
                                      ? matchingCell.threeMod
                                      : ''}{' '}
                                  </>
                                ) : (
                                  '--'
                                )}
                              </div>
                            </>
                          }
                          interactive
                          classes={{
                            tooltip: classes.tooltip,
                            arrow: classes.arrow,
                          }}
                          placement="bottom-start"
                        >
                          <MuiLink
                            color="primary"
                            className={clsx(
                              GlobalCSS.seqWordWrapwithDot,
                              classes.sequenceText
                            )}
                          >
                            {matchingCell?.sequence ? (
                              <>
                                {matchingCell.fiveMod !== Modifications.None
                                  ? matchingCell.fiveMod
                                  : ''}
                                {matchingCell.sequence}
                                {matchingCell.threeMod !== Modifications.None
                                  ? matchingCell.threeMod
                                  : ''}
                              </>
                            ) : (
                              '--'
                            )}
                          </MuiLink>
                        </Tooltip>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className={classes.tabletextAlign}>
                    <div>
                      {item.priceHeaderError ? (
                        '--'
                      ) : item.price === 'QUOTE' ? (
                        <FormattedMessage {...messages.QUOTE_ITEM} />
                      ) : item.price ? (
                        priceFormatter(
                          item.price,
                          productInformation.currencyFormat,
                          productInformation.thousandSeparator,
                          productInformation.thousandSpacing,
                          productInformation.decimalSeparator,
                          productInformation.prefixCurrecny,
                          item.currencySymbol
                        )
                      ) : (
                        '--'
                      )}
                    </div>
                    {matchingCell && (
                      <div className={classes.ngsoMixedCellItem}>
                        {matchingCell?.priceHeaderError ? (
                          '--'
                        ) : matchingCell?.price === 'QUOTE' ? (
                          <FormattedMessage {...messages.QUOTE_ITEM} />
                        ) : matchingCell?.price ? (
                          priceFormatter(
                            matchingCell.price,
                            productInformation.currencyFormat,
                            productInformation.thousandSeparator,
                            productInformation.thousandSpacing,
                            productInformation.decimalSeparator,
                            productInformation.prefixCurrecny,
                            matchingCell.currencySymbol
                          )
                        ) : (
                          '--'
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell
                    className={`${classes.tabletextAlign}
                      ${classes.cursorPointer} ${
                        isItMixedNGSOPlate ? classes.deleteButtonMixedPlate : ''
                      }`}
                  >
                    <ClearIcon onClick={() => deleteSelectedItem(item)} />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container direction="row" className={classes.priceText}>
        <Grid container className={classes.priceTotal}>
          <Grid item>
            <FormattedMessage {...messages.CONF_NGSO_PLATE_PRICE} />
          </Grid>
          <Grid item className={classes.priceAmountAlign}>
            {sessionData[selectedPlate]?.wellList[0]?.priceHeaderError
              ? '--'
              : plateTotalPrice}
          </Grid>
        </Grid>
      </Grid>
      <SubtotalAndAddToCart
        handlePrev={props.handlePrev}
        totalPrice={subTotalPrice}
        productDetails={productDetails}
        shipping={props.product?.shipping}
        saveSessionHandler={saveSessionHandler}
        configuratorAddToMiniCart={configuratorAddToMiniCart}
        clearSession={clearSession}
      />

      {dialogOpen && (
        <EditOligoName
          dialogOpen={dialogOpen}
          title={<FormattedMessage {...messages.CONF_NGSO_EDIT_OLIGO_NAME} />}
          fieldLabel={<FormattedMessage {...messages.CONF_OLIGO_NAME_NGSO} />}
          setDialogOpen={setDialogOpen}
          selectedName={selectedName}
          selectedIndex={selectedIndex}
          UpdateNewName={UpdateNewName}
        />
      )}
      {delDialogOpen && (
        <ConfirmRemovalPopup
          dialogOpen={delDialogOpen}
          setDialogOpen={setDelDialogOpen}
          selectedItem={delItem}
          onDelete={deleteOligoItem}
          type={delType}
          confirmRemoval={ngsoToEmpty}
          productType={productDetails.type}
        />
      )}
    </div>
  )
}

export default PlateOrderDetails
